<template>
  <div v-if="showDrawer" class="agent-chat-drawer">
    <!-- FAB Button -->
    <button
      class="agent-fab"
      :class="{ 'agent-fab--active': isOpen }"
      data-dd-action-name="flexiai:toggle"
      @click="toggle"
    >
      <i v-if="isOpen" class="fa fa-times" />
      <template v-else>
        <img :src="`${publicPath}icons/flexigo-icon.svg`" alt="AI Agent" class="agent-fab-icon" />
        <span class="agent-fab-label">flexiAI</span>
      </template>
    </button>

    <!-- Backdrop -->
    <transition name="agent-backdrop">
      <div v-if="isOpen" class="agent-backdrop" @click="close" />
    </transition>

    <!-- Drawer Panel: admins get the operations copilot (flexigo-assistant);
         everyone else keeps the existing rider chat, byte-for-byte. -->
    <transition name="agent-drawer">
      <div v-if="isOpen" class="agent-drawer-panel">
        <assistant-chat
          v-if="isAdminUser"
          ref="agentChat"
          :suggestion-prompts="assistantSuggestionPrompts"
          @close="close"
        />
        <agent-chat v-else ref="agentChat" :suggestion-prompts="suggestionPrompts" @close="close" />
      </div>
    </transition>
  </div>
</template>

<script>
import AgentChat from '../../screens/agent/AgentChat.vue';

export default {
  name: 'AgentChatDrawer',
  components: {
    AgentChat,
    // Async on purpose: rider sessions never render this, so they never
    // download its chunk either — the rider bundle is unchanged.
    AssistantChat: () => import('../../screens/agent/AssistantChat.vue'),
  },

  data() {
    return {
      isOpen: false,
      publicPath: process.env.BASE_URL || '/',
    };
  },

  computed: {
    hasAgentPermission() {
      return this.$store.getters.hasPermission('aiAgent:use');
    },

    /** Admin = any account with an accountType; riders have none (store isRider/isAdmin). */
    isAdminUser() {
      return Boolean(this.$store.getters.isAdmin);
    },

    /**
     * One gate for everyone: the aiAgent:use permission. Riders always had it;
     * admins now need it too (POC — provisioned per account in UMNG), on top of
     * flexigo-assistant's server-side checks (account type allowlist, kill switch).
     */
    showDrawer() {
      return this.hasAgentPermission;
    },

    assistantSuggestionPrompts() {
      // Drawn from flexigo-mcp's golden prompts (docs/golden-prompts.md) — each
      // chip showcases a distinct tool the copilot is verified on.
      return [
        { text: this.$t('flexigo.assistant.prompts.late'), icon: 'fa-clock-o' },
        { text: this.$t('flexigo.assistant.prompts.today'), icon: 'fa-tachometer' },
        { text: this.$t('flexigo.assistant.prompts.live'), icon: 'fa-map-marker' },
        { text: this.$t('flexigo.assistant.prompts.history'), icon: 'fa-history' },
        { text: this.$t('flexigo.assistant.prompts.approvals'), icon: 'fa-check-square-o' },
        { text: this.$t('flexigo.assistant.prompts.tickets'), icon: 'fa-comments-o' },
        { text: this.$t('flexigo.assistant.prompts.stops'), icon: 'fa-list-ol' },
        { text: this.$t('flexigo.assistant.prompts.rider'), icon: 'fa-user-o' },
      ];
    },

    suggestionPrompts() {
      return [
        { text: 'How to enroll in the commuter benefits program?', icon: 'fa-id-card' },
        { text: 'For what commuter benefits am I eligible?', icon: 'fa-check-circle' },
        { text: 'What transit expenses are eligible for commuter benefits?', icon: 'fa-money' },
        { text: 'What transit stations are closest to the Mountain View office?', icon: 'fa-map-marker' },
      ];
    },
  },

  watch: {
    $route() {
      if (this.isOpen) {
        this.close();
      }
    },
  },

  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
    },
    close() {
      this.isOpen = false;
      this.$nextTick(() => {
        const { agentChat } = this.$refs;
        if (agentChat && typeof agentChat.reset === 'function') {
          agentChat.reset();
        }
      });
    },
  },
};
</script>

<style scoped>
.agent-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  /* Below every overlay/panel/action bar in the app (Bootstrap dropdown 1000,
     .fixed-bottom bars 1030, sliding-panel backdrop 1040 / panel 1050): an open
     pane owns the bottom-right corner, so its footer buttons stay clickable (VSER-14372). */
  z-index: 999;
  width: 48px;
  height: 48px;
  border-radius: 24px;
  border: none;
  background: #050039;
  color: #fff;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  overflow: hidden;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(5, 0, 57, 0.4);
  transition: width 0.3s ease, background 0.2s, right 0.3s ease;
}

.agent-fab--active {
  right: 532px;
  /* Doubles as the drawer's close button: must sit above its own backdrop (1040) and panel (1045). */
  z-index: 1050;
  background: #050039;
  box-shadow: 0 4px 12px rgba(5, 0, 57, 0.3);
}

@media (max-width: 480px) {
  .agent-fab--active {
    display: none;
  }
}

.agent-fab-icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  transition: transform 0.5s ease;
}

.agent-fab:hover .agent-fab-icon {
  transform: rotate(360deg);
}

.agent-fab-label {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.3px;
  max-width: 0;
  opacity: 0;
  overflow: hidden;
  transition: max-width 0.3s ease, opacity 0.2s ease, margin 0.3s ease;
  margin-left: 0;
}

.agent-fab:hover {
  width: 130px;
  background: #1e1a4d;
}

.agent-fab:hover .agent-fab-label {
  max-width: 80px;
  opacity: 1;
  margin-left: 6px;
}

.agent-fab--active:hover {
  width: 48px;
  background: #1e1a4d;
}

.agent-fab--active:hover .agent-fab-label {
  max-width: 0;
  opacity: 0;
  margin-left: 0;
}

.agent-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1040;
}

.agent-drawer-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 520px;
  max-width: 100vw;
  z-index: 1045;
  background: #f5f7fa;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Backdrop transitions */
.agent-backdrop-enter-active,
.agent-backdrop-leave-active {
  transition: opacity 0.25s ease;
}
.agent-backdrop-enter,
.agent-backdrop-leave-to {
  opacity: 0;
}

/* Drawer slide transitions */
.agent-drawer-enter-active,
.agent-drawer-leave-active {
  transition: transform 0.3s ease;
}
.agent-drawer-enter,
.agent-drawer-leave-to {
  transform: translateX(100%);
}
</style>
